<template>
  <div class="flex flex-1 overflow-hidden">
    <tab-list
      v-model:selected-tab-id="selectedTabId"
      v-model:view-mode="viewMode"
      :tabs="tabsInfoByType[tabsType]"
      :tabs-type="tabListType"
      @tab-selected="tabSelected"
      @tab-created="tabId => (selectedTabId = tabId)"
    />
    <div v-if="viewMode === 'splitScreen' && !isMobile" class="flex-1 h-full p-4 hidden sm:block">
      <div v-if="selectedTabId" class="flex flex-col h-full overflow-hidden">
        <div class="flex justify-between gap-4 h-full overflow-hidden">
          <div class="flex flex-col w-3/5 h-full shrink-0">
            <div class="flex-1 overflow-y-scroll scrolling-touch">
              <preview-tab :tab-id="selectedTabId" @edit="editTab" />
            </div>
          </div>
          <div class="w-2/5 h-full">
            <tab-info :tab-id="selectedTabId" />
          </div>
        </div>
      </div>
      <div v-else class="h-full flex items-center justify-center">
        <l-empty-case type="empty">{{ t('tabs.empty-tabs') }}</l-empty-case>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LEmptyCase } from '@last/core-ui/paprika'

import PreviewTab from '@/modules/tabs/PreviewTab.vue'
import TabInfo from '@/modules/tabs/TabInfo.vue'
import TabList from '@/modules/tabs/TabList.vue'
import { useTabsStore } from '@/store/tabs'
import { type Tab } from '@/types'
import { isMobile } from '@/utils/device-type'

type TabsType = 'restaurant' | 'delivery' | 'takeAway' | 'toGo'

interface Props {
  tabsType?: TabsType | 'all'
}

const props = withDefaults(defineProps<Props>(), {
  tabsType: 'restaurant'
})

const tabListType = computed<TabsType>(() =>
  props.tabsType === 'all' ? 'restaurant' : props.tabsType
)

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const tabsStore = useTabsStore()
const { tabs } = storeToRefs(tabsStore)

const viewMode = ref<'splitScreen' | 'fullScreen'>('splitScreen')
const tabId = ref<string>()

watch(
  () => route.query,
  () => {
    if (route.query.preSelectedTabId) {
      router.replace({ query: {} })
      tabId.value = route.query.preSelectedTabId as string
    }
  },
  { immediate: true }
)

const selectedTabId = computed({
  get: () => tabId.value,
  set: selectedId => {
    tabId.value = selectedId
  }
})

const tabsInfoByType = computed(() => {
  return Object.values(tabs.value).reduce(
    (res, tab) => {
      res.all.push(tab)
      if (tab.pickupType === 'takeAway') {
        res.takeAway.push(tab)
        res.toGo.push(tab)
      } else if (tab.pickupType && ['delivery', 'ownDelivery'].includes(tab.pickupType)) {
        res.delivery.push(tab)
        res.toGo.push(tab)
      } else {
        res.restaurant.push(tab)
      }
      return res
    },
    { restaurant: [], delivery: [], takeAway: [], toGo: [], all: [] } as {
      restaurant: Tab[]
      delivery: Tab[]
      takeAway: Tab[]
      toGo: Tab[]
      all: Tab[]
    }
  )
})

function tabSelected(tabId: string) {
  if (isMobile.value) {
    router.push({
      name: 'orderManagement',
      params: { tabId }
    })
  }
}

function editTab() {
  router.push({
    name: 'orderManagement',
    params: { tabId: selectedTabId.value }
  })
}
</script>
